import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { secureGet, secureGetPdf, securePut, deleteWithBody, securePost } from '../../config/api';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';

import { PrimaryButton } from '../../components/PrimaryButton';
import { formatDate } from '../../utils/format';
import TableLoadingArea from '../../components/TableLoadingArea';
import Filter from '../../components/Filter';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Rows from '../../components/Rows';
import SnackbarNotification from '../../components/SnackbarNotification';
import PdfPreviewPopup from '../../components/PdfPreviewPopup';
import ConfirmPopup from '../../components/ConfirmPopup';

import deleteIcon from '../../icons/trashR.svg';
import editIcon from '../../icons/edit.svg';
import AddIcon from '../../icons/plusB.svg';
import fileIcon from '../../icons/file-textBLD.svg';

// Labelled chip opening one of a purchase's attached PDFs - the label tells the BL and the invoice apart
const DocumentButton = ({ label, title, onClick }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="h-8 px-2 inline-flex items-center gap-1 bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12] text-primary text-[12px] font-semibold hover:bg-gray-50"
  >
    <img src={fileIcon} alt="" className="w-4 h-4" />
    {label}
  </button>
);

// Provider invoice status of a stock entry, set by the billing agent
const STATUS_STYLES = {
  TO_VERIFY: { label: 'À vérifier', color: '#FEA36E' },
  PAID: { label: 'Payée', color: '#06D6A0' },
  UNPAID: { label: 'Non payée', color: '#EF466F' },
};

const StatusActionButton = ({ label, color, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="h-7 px-2 rounded-[12px] border text-[11px] font-semibold bg-white hover:bg-gray-50 disabled:opacity-50"
    style={{ borderColor: color, color }}
  >
    {label}
  </button>
);

// billing: the billing agent's "Factures fournisseurs" view - same list, read-only except the
// status, which only this view can change (TO_VERIFY -> PAID | UNPAID, UNPAID -> PAID)
const Purchases = ({ billing = false }) => {
  const [, setPurchasesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    providerId: '',
    productId: '',
    dateRange: {},
    needle: '',
    status: '',
  });
  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [isProductDeletePopupVisible, setProductDeletePopupVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // { purchaseId, path, title, filename } of the attached PDF being previewed
  const [pdfPreview, setPdfPreview] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  // { row, status } awaiting the billing agent's confirmation
  const [statusChange, setStatusChange] = useState(null);

  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const getPreviewPdfBlob = useCallback(async () => {
    if (!pdfPreview) return null;
    const response = await secureGetPdf(`/purchases/${pdfPreview.purchaseId}/${pdfPreview.path}/pdf`, showSnackbar);
    return response?.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfPreview]);

  const handleStatusChangeConfirm = async () => {
    if (!statusChange) return;
    const { row, status } = statusChange;
    setUpdatingStatusId(row.id);
    try {
      const response = await securePut(`/purchases/${row.id}/status`, { status }, showSnackbar);
      setCurrentPageData((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...response.data } : item)));
      showSnackbar(`Facture marquée « ${STATUS_STYLES[status].label} »`, 'success');
    } catch (error) {
      console.error('Error updating provider invoice status:', error);
    } finally {
      setUpdatingStatusId(null);
      setStatusChange(null);
    }
  };

  const columns = [
    {
      label: t(`fields.purchases.receptionDate`), key: 'purchaseDate', render: (date) => formatDate(date)
    },
    { label: t(`columns.purchaseNumber`), key: 'purchaseNumber' },
    { label: t(`columns.provider`), key: 'provider', render: (provider) => provider?.name },
    { label: 'Stock', key: 'repo', render: (repo) => repo?.name },
    { label: t(`columns.articleCount`), key: 'itemCount', render: (_, row) => row.products?.length || 0 },
    { label: t(`columns.totalQuantity`), key: 'totalQuantity', render: (_, row) => row.products?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0 },
    {
      label: 'Documents',
      key: 'documents',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.deliveryNoteFileUrl && (
            <DocumentButton
              label="BL"
              title="Voir le bon de livraison"
              onClick={() => setPdfPreview({
                purchaseId: row.id,
                path: 'delivery-note',
                title: `Bon de livraison - ${row.purchaseNumber}`,
                filename: `BL-${row.purchaseNumber}.pdf`,
              })}
            />
          )}
          {row.invoiceFileUrl && (
            <DocumentButton
              label="FA"
              title="Voir la facture fournisseur"
              onClick={() => setPdfPreview({
                purchaseId: row.id,
                path: 'invoice',
                title: `Facture fournisseur - ${row.purchaseNumber}`,
                filename: `Facture-${row.purchaseNumber}.pdf`,
              })}
            />
          )}
        </div>
      ),
    },
    {
      label: t('columns.status'),
      key: 'status',
      render: (status, row) => {
        const current = STATUS_STYLES[status] || STATUS_STYLES.TO_VERIFY;
        const busy = updatingStatusId === row.id;
        return (
          <div className="flex flex-col items-start gap-1">
            <span
              className="text-white text-[12px] font-semibold px-2 py-1 rounded-full inline-block w-fit text-center"
              style={{ backgroundColor: current.color }}
              title={row.verifiedBy ? `Par ${row.verifiedBy}` : undefined}
            >
              {current.label.toUpperCase()}
            </span>
            {billing && status !== 'PAID' && (
              <div className="flex items-center gap-1">
                <StatusActionButton label="Payée" color={STATUS_STYLES.PAID.color} disabled={busy} onClick={() => setStatusChange({ row, status: 'PAID' })} />
                {status !== 'UNPAID' && (
                  <StatusActionButton label="Non payée" color={STATUS_STYLES.UNPAID.color} disabled={busy} onClick={() => setStatusChange({ row, status: 'UNPAID' })} />
                )}
              </div>
            )}
          </div>
        );
      },
    },
    !billing && {
      label: ' ',
      key: 'actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {/* Only a stock entry whose invoice is still "À vérifier" can be edited */}
          {row.status === 'TO_VERIFY' && (
            <button
              onClick={() => history.push(`/purchases/modify/${row.id}`)}
              className="w-8 h-8 flex items-center justify-center bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
            >
              <img src={editIcon} alt="Edit" width={16} height={16} />
            </button>
          )}
          <button
            onClick={() => handleDeleteClick(row)}
            className="w-8 h-8 flex items-center justify-center bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
          >
            <img src={deleteIcon} alt="Delete" width={16} height={16} />
          </button>
        </div>
      ),
    },
  ].filter(Boolean);

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    { name: 'dateRange', type: 'daterange' },
    {
      name: 'providerId',
      label: t(`filters.purchases.provider`),
      type: 'dropdown',
      options: providers,
    },
    {
      name: 'productId',
      label: t(`filters.purchases.product`),
      type: 'dropdown',
      options: products,
    },
    {
      name: 'status',
      label: t('columns.status'),
      type: 'dropdown',
      options: Object.entries(STATUS_STYLES).map(([value, { label }]) => ({ value, label })),
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const providerResponse = await secureGet('/providers', showSnackbar, { signal });
        const providerOptions = providerResponse.data.map((provider) => ({
          value: provider.id,
          label: provider.name,
        }));
        setProviders(providerOptions);

        const productsResponse = await secureGet("/products/unpaginated", showSnackbar, { signal });
        const productsOptions = productsResponse.data.map((product) => ({
          value: product.id,
          reference: product.reference,
          name: product.name,
        }));
        setProducts(productsOptions);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching dropdown options:', error);
        }
      }
    };

    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedPurchases = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const requestBody = {
        providerId: activeFilters.providerId || null,
        productId: activeFilters.productId || null,
        startDate: activeFilters.dateRange?.startDate || null,
        endDate: activeFilters.dateRange?.endDate || null,
        needle: activeFilters.needle || null,
        status: activeFilters.status || null,
      };
      console.log('Request Body:', requestBody);

      const response = await securePost(`/purchases?page=${page}&size=${size}`, requestBody, showSnackbar, { signal });
      console.log('Received Purchases:', response.data);

      const { content, totalPages, totalElements } = response.data;
      setPurchasesData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
      setTotalElements(totalElements);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching purchases:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    fetchPaginatedPurchases(0);
  }, [fetchPaginatedPurchases]);


  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    fetchPaginatedPurchases(0, newPageSize, filters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPurchases(page, pageSize, filters);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    console.log('itemToDelete', itemToDelete);

    try {
      await deleteWithBody(`/purchases/delete`, { purchaseId: itemToDelete.id }, showSnackbar);
      setPurchasesData((prev) => prev.filter((item) => item.purchaseNumber !== itemToDelete.purchaseNumber));
      setCurrentPageData((prev) => prev.filter((item) => item.purchaseNumber !== itemToDelete.purchaseNumber));
      setDeletePopupVisible(false);

      showSnackbar(t(`confirmation.purchaseDelete`), 'success');
    } catch (error) {
      console.error('Error deleting purchase:', error);
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setFilters({ providerId: '', productId: '', dateRange: {}, needle: '', status: '' });
    fetchPaginatedPurchases(0);
  };

  const handleFilterChange = (filterName, filterValue) => {
    console.log('Filter Changed:', filterName, filterValue);
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      updatedFilters.dateRange = {
        startDate: filterValue?.startDate || null,
        endDate: filterValue?.endDate || null,
      };
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedPurchases(0, pageSize, updatedFilters);
  };

  const handleProductDelete = (purchase, product) => {
    setItemToDelete(purchase);
    setProductToDelete(product);
    setProductDeletePopupVisible(true);
  };

  const handleProductDeleteConfirm = async () => {
    if (!itemToDelete || !productToDelete) return;
    setDeletingProduct(true);
    console.log("Deleting product:", productToDelete);

    try {
      await deleteWithBody(`/purchases/delete`, {
        purchaseId: itemToDelete.id,
        productId: productToDelete.product?.id || productToDelete.id,
      });

      await fetchPaginatedPurchases(currentPage, pageSize, filters);

      setProductDeletePopupVisible(false);
      showSnackbar(t(`confirmation.purchaseDelete`), 'success');
    } catch (error) {
      console.error("Error deleting product:", error);
      showSnackbar(t(`failure.purchaseDelete`), 'error');
    } finally {
      setDeletingProduct(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Drivnest - {t(billing ? "titles.providerInvoices" : "titles.purchases")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(billing ? `titles.providerInvoices` : `titles.purchases`)}</h1>
        <div className="ml-auto text-[16px] ">
          {!billing && (
            <PrimaryButton
              label={t(`primaryButtons.add_purchase`)}
              icon={AddIcon}
              onClick={() => history.push("/purchases/add")}
            />
          )}
        </div>
      </div>

      <Filter filters={filterOptions} onFilterChange={handleFilterChange} resetFilters={resetFilters} filterValues={filters} />

      <TableLoadingArea loading={loading} minHeight={200}>
        <Rows columns={columns} data={currentPageData} expandable={true} onDelete={billing ? undefined : handleProductDelete} />
        <PageScroller
          pageNumber={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </TableLoadingArea>

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} loading={deleting} />
      )}

      {isProductDeletePopupVisible && (
        <DeletePopup
          open={isProductDeletePopupVisible}
          onCancel={() => setProductDeletePopupVisible(false)}
          onConfirm={handleProductDeleteConfirm}
          loading={deletingProduct}
        />
      )}

      <ConfirmPopup
        open={!!statusChange}
        message={statusChange && `Marquer la facture ${statusChange.row.purchaseNumber} comme « ${STATUS_STYLES[statusChange.status].label} » ?`}
        warning={statusChange?.status === 'PAID' ? 'Ce statut est définitif et ne pourra plus être modifié.' : undefined}
        color={statusChange ? STATUS_STYLES[statusChange.status].color : undefined}
        loading={!!updatingStatusId}
        onCancel={() => setStatusChange(null)}
        onConfirm={handleStatusChangeConfirm}
      />

      <PdfPreviewPopup
        open={!!pdfPreview}
        title={pdfPreview?.title}
        filename={pdfPreview?.filename}
        getPdfBlob={getPreviewPdfBlob}
        onClose={() => setPdfPreview(null)}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Purchases;
